<template>
    <tag
        :is="tag"
        :href="(tag == 'a' || tag == 'router-link') && href !== null ? href : false"
        :to="tag == 'router-link' && href !== null ? href : false"
        :class="className"
    >
        <Icon v-if="iconBefore" class="c-btn__icon" :icon="iconBefore" />
        <span class="c-btn__label">
            <slot>{{ label }}</slot>
        </span>

        <Icon v-if="iconAfter" class="c-btn__icon" :icon="iconAfter" />
    </tag>
</template>

<script>
import Icon from "objects/Icon";

export default {
    name: "Btn",
    components: {
        Icon
    },
    props: {
        tag: {
            type: String,
            default: "button"
        },
        href: {
            type: String,
            default: null
        },
        label: {
            type: String,
            default: "Button Label"
        },
        iconBefore: {
            type: String,
            default: null
        },
        iconAfter: {
            type: String,
            default: null
        },
        color: {
            type: String,
            default: null
        },
        small: {
            type: Boolean,
            default: false
        },
        isFullWidth: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        className() {
            let classname = "c-btn";

            if (this.color) {
                classname += ` -color-${this.color}`;
            }

            if (this.small) {
                classname += " -sm";
            }

            if (this.iconAfter) {
                classname += ` -icon-after`;
            }
            if (this.iconBefore) {
                classname += ` -icon-before`;
            }

            if (this.isFullWidth) {
                classname += ` -full-width`;
            }

            return classname;
        }
    }
};
</script>

<style lang="scss">
.c-btn {
    --height: #{$btn-height};

    --color-text: #{$color-lighter};
    --color-bg: #{$color-dark};
    --color-icon-bg: transparent;
    --color-icon-text: var(--color-text);

    display: inline-flex;
    align-items: center;
    height: var(--height);
    color: var(--color-text);

    border-radius: calc(var(--height) / 2);
    box-shadow: $box-shadow;
    cursor: pointer;
    overflow: hidden;
    transition: color 0.8s $out-expo;
    -webkit-mask-image: -webkit-radial-gradient(white, black);

    &.-full-width {
        background-color: var(--color-bg);
    }

    &.-sm {
        --height: #{$btn-height-sm};
    }

    &.-color-bright {
        //--color-text: #{$color-lighter};
        --color-bg: #{$color-dark};
        --color-icon-bg: #{$color-bright};
        --color-text: #{$color-dark};
        --color-icon-text: #{$color-lighter};
    }

    &.-color-lighter {
        --color-bg: #{$color-lighter};
        --color-text: #{$color-dark};
        --color-icon-bg: #{$color-dark};
        --color-icon-text: #{$color-lighter};
    }

    &.-color-lighter.-full-width {
        --color-text: #{$color-lighter};
        --color-bg: #{$color-dark};
        --color-icon-bg: transparent;
        --color-icon-text: var(--color-text);
    }

    &:hover {
        --color-text: #{$color-dark};

        &.-color-bright,
        &.-color-lighter {
            --color-icon-bg: #{$color-bright};
            --color-text: #{$color-dark};
        }

        .c-btn__label {
            &:before {
                transform: translate(calc(0% - var(--height)), 0);
                transform-origin: 0 50%;
            }
            &:after {
                transform: translate(calc(0% + var(--height)), 0);
                transform-origin: 0 50%;
            }
        }

        .is-edge & {
            color: $color-lighter;

            .c-btn__label {
                opacity: 0.7;
            }
        }
    }
    &.-full-width {
        &:hover {
            .c-btn__label {
                &:after,
                &:before {
                    transform: translate(0, 0);
                    transform-origin: 0 50%;
                }
            }
        }
    }
}

.c-btn__label {
    z-index: 0;
    position: static;
    padding-right: calc(var(--height) / 2);
    padding-left: calc(var(--height) / 2);

    &:before {
        @include pseudo-el($bg: $color-light);
        z-index: -1;
        position: absolute;
        top: 0;
        left: calc(var(--height));
        /* width: calc(100% + var(--height)); */
        /* left: 0; */
        /* left: calc(100% - var(--height) / 2); */
        /* right: calc(var(--height) / 2); */
        border-radius: calc(var(--height) / 2);
        transition: transform 0.8s $out-expo;

        /* look super weird this way */
        /* transform: translate(0, 0);
    transform-origin: 0 50%; */

        transform: translate(-100%, 0);
        transform-origin: 100% 50%;

        .-full-width & {
            top: 0;
            left: 0;
            border-radius: calc(var(--height) / 2);
            transform: translate(-100%, 0);
        }
        .-icon-after & {
            display: none;
        }
    }
    &:after {
        @include pseudo-el($bg: $color-light);
        z-index: -1;
        position: absolute;
        top: 0;
        /* left: calc(100% + var(--height)); */
        /* right: calc(- 100% + var(--height)); */
        right: calc(var(--height));
        /* right: 0px; */
        /* width: calc(100% + var(--height)); */
        /* left: 0; */
        /* left: calc(100% - var(--height) / 2); */
        /* right: calc(var(--height) / 2); */

        border-radius: calc(var(--height) / 2);
        transition: transform 0.8s $out-expo;

        transform: translate(calc(0% + var(--height)), 0);
        transform-origin: 0 50%;

        @media #{$breakpoint-horizontal} {
            transform: translate(100%, 0);
            transform-origin: 100% 50%;
        }

        .-full-width & {
            top: 0;
            left: 0;
            right: 0;
            border-radius: calc(var(--height) / 2);
            transform: translate(-100%, 0);
        }
        .-icon-before & {
            display: none;
        }
    }

    .is-edge & {
        transition: opacity 0.3s $out-quad;
    }
}

.c-btn__icon {
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--height);
    height: var(--height);
    color: var(--color-icon-text);
    border-radius: 50%;
    background-color: var(--color-icon-bg);
    transition: background-color 0.8s $out-expo;

    & + .c-btn__label {
        padding-left: calc(var(--height) / 4);
    }
}
</style>
